/* eslint-disable max-len */
/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import React from 'react';
import { Button } from 'react-toolbox/lib/button';
import cx from 'classnames';

import Card from 'components/Card';
import StyledSelect from 'components/StyledSelect';
import BasicYesNoQuestion from './basicYesNoQuestion';
import ChecklistYesNoQuestion from './checklistYesNoQuestion';
import ChecklistRescheduler from './checklistRescheduler';
import ChecklistResponseNote from './checklistResponseNote';
import { REMOVAL_OPTIONS } from 'config/constants';
import styles from 'styles/form';
import S from '../styles.scss';

const ChecklistPhaseSpecifics = (props) => {
  const {
    phase: { vendor_eta = null, surface_type: { id: surfaceId }, phase_specific: {
      customer_has_water,
      need_move_furniture,
      need_move_furniture_oversized,
      need_move_appliances,
      need_remove_replace_toilet,
      need_remove_floating_hardwood,
      need_remove_gluedown_carpet,
      need_remove_gluedown_hardwood,
      need_remove_gluedown_vinyl,
      need_remove_laminate,
      need_remove_naildown_hardwood,
      need_remove_over_pad_carpet,
      need_remove_tile,
      need_remove_vinyl_plank,
    } },
    powerAnswered,
    climateControlAnswered,
    waterAnswered,
    furnitureAnswered,
    furnitureOversizedAnswered,
    appliancesAnswered,
    toiletAnswered,
    removalAnswered,
    selectedRemovalType,
    newDate,
    newDateNote,
    newDateError,
    haltMessage,
    showFurnitureNote,
    showFurnitureNoteAnswered,
    showAppliancesNote,
    showAppliancesNoteAnswered,
    showToiletNote,
    showToiletNoteAnswered,
    handleBasicYesNo,
    handleSpecificAnswered,
    handleRemovalType,
    handleNewDate,
    handleNewDateNote,
    submitNewSpecifics,
    submitNewDate,
    position,
    errors,
  } = props;
  const waterRequired = [3, 7].includes(surfaceId);
  const removalValues = [
    need_remove_floating_hardwood,
    need_remove_gluedown_carpet,
    need_remove_gluedown_hardwood,
    need_remove_gluedown_vinyl,
    need_remove_laminate,
    need_remove_naildown_hardwood,
    need_remove_over_pad_carpet,
    need_remove_tile,
    need_remove_vinyl_plank,
  ];
  const powerConfirmed = powerAnswered === 'yes';
  const climateControlConfirmed = climateControlAnswered === 'yes';
  const waterConfirmed = !waterRequired || (customer_has_water === true || waterAnswered === 'yes');
  const furnitureConfirmed = (need_move_furniture === true || furnitureAnswered.length > 0);
  const furnitureOversizedConfirmed = (need_move_furniture_oversized === true || furnitureOversizedAnswered.length > 0);
  const furnitureQuestionsConfirmed = furnitureAnswered === 'yes' ? furnitureOversizedConfirmed : furnitureConfirmed;
  const appliancesQuestionsConfirmed = appliancesAnswered === 'no' || showAppliancesNoteAnswered.length > 0;
  const toiletQuestionsConfirmed = (toiletAnswered === 'no' || showToiletNoteAnswered.length > 0);
  const removalConfirmed = (removalValues.includes(true) || removalAnswered === 'yes' || (removalAnswered === 'no' && selectedRemovalType.length > 0));
  const showWater = (
    waterRequired && (!customer_has_water || waterAnswered.length > 0) &&
    (powerConfirmed && climateControlConfirmed)
  );
  const showFurniture = (
    (!need_move_furniture || furnitureAnswered.length > 0) &&
    (powerConfirmed && climateControlConfirmed && waterConfirmed)
  );
  const showFurnitureNoteQuestion = (
    (need_move_furniture || furnitureAnswered === 'yes') &&
    (powerConfirmed && climateControlConfirmed && waterConfirmed)
  );
  const showFurnitureOversized = (
    (!need_move_furniture_oversized || furnitureOversizedAnswered.length > 0) &&
    (powerConfirmed && climateControlConfirmed && waterConfirmed && furnitureAnswered === 'yes')
  );
  const showAppliances = (
    (!need_move_appliances || appliancesAnswered) &&
    (powerConfirmed && climateControlConfirmed && waterConfirmed && furnitureQuestionsConfirmed)
  );
  const showAppliancesNoteQuestion = (
    (need_move_appliances || appliancesAnswered === 'yes') &&
    (powerConfirmed && climateControlConfirmed && waterConfirmed && furnitureQuestionsConfirmed)
  );
  const showToilet = (
    (!need_remove_replace_toilet || toiletAnswered) &&
    (powerConfirmed && climateControlConfirmed && waterConfirmed &&
      furnitureQuestionsConfirmed && appliancesQuestionsConfirmed)
  );
  const showToiletNoteQuestion = (
    (need_remove_replace_toilet || toiletAnswered === 'yes') &&
    (powerConfirmed && climateControlConfirmed && waterConfirmed &&
      furnitureQuestionsConfirmed && appliancesQuestionsConfirmed)
  );
  const showRemoval = (
    (!removalValues.includes(true) || removalAnswered) &&
    (powerConfirmed && climateControlConfirmed && waterConfirmed &&
      furnitureQuestionsConfirmed && appliancesQuestionsConfirmed && toiletQuestionsConfirmed)
  );
  const allQuestionsAnswered = (
    powerConfirmed && climateControlConfirmed && waterConfirmed &&
    furnitureQuestionsConfirmed && appliancesQuestionsConfirmed &&
    toiletQuestionsConfirmed && removalConfirmed
  );
  const showCompleteButton = allQuestionsAnswered;

  const largeInput = cx(styles.inputs, styles.largeInput);

  return (<Card className={S.card}>
    <div className={S.headerContainer}>
      <h4 className={S.title}>Scheduling Checklist Phase Specifics</h4>
    </div>
    <ChecklistResponseNote
      noteKey={position === 'Customer Care Specialist' ? 'flagNote' : 'workroomNote'}
    />
    <ChecklistYesNoQuestion
      phaseSpecific
      canShow
      text="Will the power be on and available for our installers to use during the installation?"
      yesDisabled={powerAnswered === 'yes'}
      noDisabled={powerAnswered === 'no'}
      handleAnswered={handleSpecificAnswered}
      answeredArgs={['powerAnswered', 'customer_has_power']}
    />
    <ChecklistYesNoQuestion
      phaseSpecific
      canShow={powerConfirmed}
      text="Will your home be climate controlled before, during, and after the installation?"
      yesDisabled={climateControlAnswered === 'yes'}
      noDisabled={climateControlAnswered === 'no'}
      handleAnswered={handleSpecificAnswered}
      answeredArgs={['climateControlAnswered', 'customer_has_climate_control']}
    />
    <ChecklistYesNoQuestion
      phaseSpecific
      canShow={showWater}
      text="Is the water turned on?"
      yesDisabled={waterAnswered === 'yes'}
      noDisabled={waterAnswered === 'no'}
      handleAnswered={handleSpecificAnswered}
      answeredArgs={['waterAnswered', 'customer_has_water']}
    />
    <ChecklistYesNoQuestion
      phaseSpecific
      canShow={showFurniture}
      text="Will we be moving any furniture from the room(s) we\'re installing in? It is currently notated that we aren\'t."
      yesDisabled={furnitureAnswered === 'yes'}
      noDisabled={furnitureAnswered === 'no'}
      handleAnswered={handleSpecificAnswered}
      answeredArgs={['furnitureAnswered', 'need_move_furniture']}
      showNote={furnitureAnswered === 'yes'}
      noteKey="priceAdjust"
    />
    <BasicYesNoQuestion
      parentAnswer={furnitureAnswered}
      canShow={showFurnitureNoteQuestion}
      yesDisabled={showFurnitureNoteAnswered === 'yes'}
      noDisabled={showFurnitureNoteAnswered === 'no'}
      handleAnswered={handleBasicYesNo}
      showKey="showFurnitureNote"
      showNote={showFurnitureNote}
    />
    <ChecklistYesNoQuestion
      phaseSpecific
      canShow={showFurnitureOversized}
      text="Is any of it an armoire, large entertainment center, 3-piece or larger sectional sofa, dresser with mirror, nut & bolt bed, or 4 post bed?"
      yesDisabled={furnitureOversizedAnswered === 'yes'}
      noDisabled={furnitureOversizedAnswered === 'no'}
      handleAnswered={handleSpecificAnswered}
      answeredArgs={['furnitureOversizedAnswered', 'need_move_furniture_oversized']}
    />
    <ChecklistYesNoQuestion
      phaseSpecific
      canShow={showAppliances}
      text="Will we be moving any appliances from the room(s) we\'re installing in? It is currently notated that we aren\'t."
      yesDisabled={appliancesAnswered === 'yes'}
      noDisabled={appliancesAnswered === 'no'}
      handleAnswered={handleSpecificAnswered}
      answeredArgs={['appliancesAnswered', 'need_move_appliances']}
      showNote={appliancesAnswered === 'yes'}
      noteKey="priceAdjust"
    />
    <BasicYesNoQuestion
      parentAnswer={appliancesAnswered}
      canShow={showAppliancesNoteQuestion}
      yesDisabled={showAppliancesNoteAnswered === 'yes'}
      noDisabled={showAppliancesNoteAnswered === 'no'}
      handleAnswered={handleBasicYesNo}
      showKey="showAppliancesNote"
      showNote={showAppliancesNote}
    />
    <ChecklistYesNoQuestion
      phaseSpecific
      canShow={showToilet}
      text="Will we be removing and/or replacing a toilet? It is currently notated that we aren\'t."
      yesDisabled={toiletAnswered === 'yes'}
      noDisabled={toiletAnswered === 'no'}
      handleAnswered={handleSpecificAnswered}
      answeredArgs={['toiletAnswered', 'need_remove_replace_toilet']}
      showNote={toiletAnswered === 'yes'}
      noteKey="priceAdjust"
    />
    <BasicYesNoQuestion
      parentAnswer={toiletAnswered}
      canShow={showToiletNoteQuestion}
      yesDisabled={showToiletNoteAnswered === 'yes'}
      noDisabled={showToiletNoteAnswered === 'no'}
      handleAnswered={handleBasicYesNo}
      showKey="showToiletNote"
      showNote={showToiletNote}
    />
    <ChecklistYesNoQuestion
      phaseSpecific
      canShow={showRemoval}
      text="I\'m showing we will not be removing any existing flooring from the rooms we\'re installing in. Is that correct?"
      yesDisabled={removalAnswered === 'yes'}
      noDisabled={removalAnswered === 'no'}
      handleAnswered={handleSpecificAnswered}
      answeredArgs={['removalAnswered', '']}
      showNote={removalAnswered === 'no'}
      noteKey="priceAdjust"
    />
    {removalAnswered === 'no' && <div className={S.checklistContainer}>
      <StyledSelect
        className={largeInput}
        label="Type Of Removal"
        onChange={handleRemovalType}
        options={REMOVAL_OPTIONS}
        value={selectedRemovalType}
        placeholder="What Type Of Flooring?"
        required
      />
    </div>}
    {showCompleteButton && <div className={S.checklistContainer}>
      <p>{vendor_eta && surfaceId === 2 ?
        `Your carpet is due to arrive ${vendor_eta}.
Let's take a look at the schedule and see what day after that we can get you scheduled.
If at any point your carpet is delayed in arriving to our warehouse,
we will reach out to you to get a new schedule date set.` :
        'OK, let\'s get you scheduled!'}
      </p>
      <br />
      <Button
        label="COMPLETE CHECKLIST"
        flat inverse ripple
        disabled={false}
        className={S.saveButton}
        onClick={() => submitNewSpecifics()}
      />
    </div>}
    {haltMessage && <ChecklistRescheduler
      condition={haltMessage.length > 0}
      text={haltMessage}
      newDate={newDate}
      handleNewDate={handleNewDate}
      errors={errors}
      newDateNote={newDateNote}
      handleNewDateNote={handleNewDateNote}
      submitNewDate={submitNewDate}
      newDateError={newDateError}
    />}
  </Card>);
};

ChecklistPhaseSpecifics.propTypes = {
  position: PropTypes.string,
  phase: PropTypes.object,
  powerAnswered: PropTypes.string,
  climateControlAnswered: PropTypes.string,
  waterAnswered: PropTypes.string,
  furnitureAnswered: PropTypes.string,
  furnitureOversizedAnswered: PropTypes.string,
  appliancesAnswered: PropTypes.string,
  toiletAnswered: PropTypes.string,
  removalAnswered: PropTypes.string,
  selectedRemovalType: PropTypes.string,
  newDate: PropTypes.string,
  newDateNote: PropTypes.string,
  newDateError: PropTypes.string,
  haltMessage: PropTypes.string,
  showFurnitureNote: PropTypes.bool,
  showFurnitureNoteAnswered: PropTypes.string,
  showAppliancesNote: PropTypes.bool,
  showAppliancesNoteAnswered: PropTypes.string,
  showToiletNote: PropTypes.bool,
  showToiletNoteAnswered: PropTypes.string,
  handleBasicYesNo: PropTypes.func,
  handleSpecificAnswered: PropTypes.func,
  handleRemovalType: PropTypes.func,
  handleNewDate: PropTypes.func,
  handleNewDateNote: PropTypes.func,
  submitNewSpecifics: PropTypes.func,
  submitNewDate: PropTypes.func,
  errors: PropTypes.object,
};

export default ChecklistPhaseSpecifics;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseSchedulingChecklist/components/checklistPhaseSpecifics.jsx